import { Fragment, useCallback, useMemo, useState } from "react";

import {
  Box,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListSubheader,
  Paper,
  Typography,
} from "@material-ui/core";

import ApartmentIcon from "@material-ui/icons/Apartment";
import MenuIcon from "@material-ui/icons/Menu";
import EventIcon from "@material-ui/icons/Event";
import MeetingRoomIcon from "@material-ui/icons/MeetingRoom";
import LocalCarWashIcon from "@material-ui/icons/LocalCarWash";
import PlaceIcon from "@material-ui/icons/Place";
import CheckBoxOutlineBlankIcon from "@material-ui/icons/CheckBoxOutlineBlank";
import LinkIcon from "@material-ui/icons/Link";
import FormatListBulletedIcon from "@material-ui/icons/FormatListBulleted";
import AssignmentLateIcon from "@material-ui/icons/AssignmentLate";
import PaymentIcon from "@material-ui/icons/Payment";
import ErrorIcon from "@material-ui/icons/Error";
import ReceiptIcon from "@material-ui/icons/Receipt";
import BookmarksIcon from "@material-ui/icons/Bookmarks";
import AssignmentIndIcon from "@material-ui/icons/AssignmentInd";
import QuestionAnswerIcon from "@material-ui/icons/QuestionAnswer";
import PeopleIcon from "@material-ui/icons/People";

import { ServiceReservationStatus } from "../../components";
import {
  ComposableServiceConfigPaymentMode,
  ServiceReservation,
  ServiceType,
  ServiceTypeListItemType,
} from "../../../graphql/models";
import { MamacleanReservationInfobox } from "../mamaclean_reservation_infobox";
import ServiceReservationPayAfterData from "../service_reservation_payafter_data/service_reservation_payafter_data";
import { ServiceReservationSurvey } from "../service_reservation_survey/service_reservation_survey";
import { useDateTimeUtils, useFormatter } from "../../../hooks";
import { ServiceReservationPrice } from "../service_reservation_price";

export interface ServiceReservationPreviewProps {
  serviceReservation: ServiceReservation;
  onServiceReservationStatusUpdated?: () => void;
}

export function ServiceReservationPreview(
  props: ServiceReservationPreviewProps
) {
  const { serviceReservation: SR, onServiceReservationStatusUpdated } = props;
  const [serviceReservation, setServiceReservation] = useState(SR);

  const [sendPush, setSendPush] = useState(false);
  const [consentStatusChange, setConsentStatusChange] = useState(true);

  const { displayDate } = useDateTimeUtils();
  const { currency } = useFormatter();

  // survey
  const { survey } = SR;
  const showSurvey = useMemo(() => survey && survey.length > 0, [survey]);

  const isDeleted = useMemo(
    () => Boolean(serviceReservation?.deletionDate),
    [serviceReservation.deletionDate]
  );

  const isInvoiceRequested = useMemo(
    () => serviceReservation?.invoiceRequested === true,
    [serviceReservation.invoiceRequested]
  );

  const isCarWashReservation = useMemo(() => {
    return serviceReservation.service.type === ServiceType.CARWASH;
  }, [serviceReservation]);

  const isLongevityReservation = useMemo(() => {
    return serviceReservation.service.type === ServiceType.LONGEVITY;
  }, [serviceReservation]);

  const hasLinkedReservation = useMemo(() => {
    return Boolean(serviceReservation.linkedAmenityReservation);
  }, [serviceReservation]);

  const reservationServices = useMemo(() => {
    return serviceReservation?.serviceTypeListItems?.filter(
      (v) => v.type === ServiceTypeListItemType.SERVICE
    );
  }, [serviceReservation]);

  const reservationProducts = useMemo(() => {
    return serviceReservation?.serviceTypeListItems?.filter(
      (v) => v.type === ServiceTypeListItemType.PRODUCT
    );
  }, [serviceReservation]);

  const linkedAmenityReservationDate = useMemo(() => {
    if (
      serviceReservation &&
      serviceReservation.linkedAmenityReservation?.startDate
    ) {
      return new Date(
        serviceReservation.linkedAmenityReservation.startDate
      ).toLocaleString();
    }
  }, [serviceReservation]);

  const isMamacleanReservation = useMemo(() => {
    return serviceReservation.service.type === ServiceType.MAMACLEAN;
  }, [serviceReservation.service]);

  const showPayAfterData = useMemo(() => {
    let result = false;
    const { service } = serviceReservation;
    if (service?.paymentMode === ComposableServiceConfigPaymentMode.PAY_AFTER) {
      result = true;
    }
    return result;
  }, [serviceReservation]);

  const setPayAfterData = useCallback(
    (
      newAmount: number,
      newDate: Date | undefined,
      sendPush: boolean,
      pushMessage: string | undefined
    ) => {
      setServiceReservation({
        ...serviceReservation,
        totalPrice: newAmount,
        lastTimeForPayment: newDate,
        pushMessage,
      });
      setSendPush(sendPush);
    },
    [serviceReservation]
  );

  return (
    <Paper elevation={3} style={{ width: "100%", padding: "1rem" }}>
      <List
        component="nav"
        aria-labelledby={`reservation-${serviceReservation?.id}-reservation-data`}
        style={{ width: "100%" }}
        subheader={
          <>
            <ListSubheader
              component="div"
              id={`reservation-${serviceReservation?.id}-reservation-data-subheader`}
            >
              Informazioni prenotazione
            </ListSubheader>
          </>
        }
      >
        {isDeleted && serviceReservation && serviceReservation.deletionDate && (
          <ListItem>
            <ListItemIcon>
              <ErrorIcon />
            </ListItemIcon>
            <Typography variant="caption">
              ATTENZIONE! La prenotazione è stata annullata il{" "}
              {displayDate(serviceReservation.deletionDate)}
            </Typography>
          </ListItem>
        )}
        {serviceReservation && hasLinkedReservation && (
          <Fragment>
            <ListItem>
              <ListItemIcon>
                <PlaceIcon />
              </ListItemIcon>
              <Typography variant="caption">
                {serviceReservation.linkedAmenityReservation?.service?.name}
              </Typography>
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <LinkIcon />
              </ListItemIcon>
              <Typography variant="caption">
                Prenotazione amenity collegata: #
                {serviceReservation?.linkedAmenityReservation?.code}. <br />
                Prenotata per la data: {linkedAmenityReservationDate}
              </Typography>
            </ListItem>
          </Fragment>
        )}
        <ListItem>
          <ListItemIcon>
            <BookmarksIcon />
          </ListItemIcon>
          <Typography variant="caption">
            Codice Prenotazione: <strong>{serviceReservation?.code}</strong>
          </Typography>
        </ListItem>
        <ListItem>
          <ListItemIcon>
            <PeopleIcon></PeopleIcon>
          </ListItemIcon>
          <Typography variant="caption">
            <strong>
              {serviceReservation.userDetails?.firstName}{" "}
              {serviceReservation.userDetails?.lastName}
            </strong>
            <br />
            <span>T. {serviceReservation.userDetails?.phone}</span>
            <br />
            <span>@. {serviceReservation.userDetails?.email}</span>
          </Typography>
        </ListItem>
        <ListItem>
          <ListItemIcon>
            <EventIcon />
          </ListItemIcon>
          <Typography variant="caption">
            {serviceReservation.bookingTexts?.rangeText}
          </Typography>
        </ListItem>
        {showPayAfterData && (
          <ServiceReservationPayAfterData
            reservation={serviceReservation}
            onUpdate={setPayAfterData}
            consentStatusChange={setConsentStatusChange}
          />
        )}
        {serviceReservation && (
          <ListItem>
            <ListItemIcon>
              <PaymentIcon />
            </ListItemIcon>
            <Box
              style={{
                display: "flex",
                flexDirection: "row",
                gap: "1rem",
                alignItems: "center",
                justifyContent: "flex-start",
              }}
            >
              <ServiceReservationStatus
                serviceReservation={serviceReservation}
                onStatusUpdated={onServiceReservationStatusUpdated}
                status={serviceReservation.status}
                sendPushOnStatusChange={sendPush}
                enableStatusChange={consentStatusChange}
              />
              <ServiceReservationPrice reservation={serviceReservation} />
            </Box>
          </ListItem>
        )}
        {serviceReservation?.orderCoupon && (
          <ListItem>
            <ListItemIcon>
              <PaymentIcon />
            </ListItemIcon>
            <Typography variant="caption">
              Utilizzato coupon {serviceReservation.orderCoupon.code}
            </Typography>
          </ListItem>
        )}
        {!isDeleted && isInvoiceRequested && serviceReservation && (
          <ListItem>
            <ListItemIcon>
              <ReceiptIcon />
            </ListItemIcon>
            <Typography variant="caption">
              <strong>
                Il cliente ha richiesto l'emissione della fattura per questa
                prestazione.
              </strong>
            </Typography>
          </ListItem>
        )}
        <ListItem>
          <ListItemIcon>
            <ApartmentIcon />
          </ListItemIcon>
          <Typography variant="caption">
            {serviceReservation?.building.name}
          </Typography>
        </ListItem>
        {serviceReservation?.apartment && (
          <ListItem>
            <ListItemIcon>
              <MeetingRoomIcon />
            </ListItemIcon>
            <Typography variant="caption">
              {serviceReservation?.apartment.code}
            </Typography>
          </ListItem>
        )}
        {showSurvey && (
          <ListItem>
            <ListItemIcon>
              <QuestionAnswerIcon />
            </ListItemIcon>
            <ServiceReservationSurvey items={survey || []} />
          </ListItem>
        )}
        {isCarWashReservation && (
          <>
            <ListItem>
              <ListItemIcon>
                <LocalCarWashIcon />
              </ListItemIcon>
              <Typography variant="caption">
                {serviceReservation?.vehicle?.model}
              </Typography>
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <MenuIcon />
              </ListItemIcon>
              <Typography variant="caption">
                Targa: {serviceReservation?.vehicle?.plate}
              </Typography>
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckBoxOutlineBlankIcon />
              </ListItemIcon>
              <Typography variant="caption">
                Box: #{serviceReservation?.vehicle?.box}
              </Typography>
            </ListItem>
          </>
        )}
        {isLongevityReservation &&
          serviceReservation.slots.map((slot) => {
            return slot.reservationOptions?.map((opt) => (
              <ListItem>
                <ListItemIcon>
                  <AssignmentIndIcon />
                </ListItemIcon>
                <Typography variant="caption">
                  Richiesto {opt.bookableUnits} professionista per{" "}
                  {opt.listItemCategoryName}
                </Typography>
              </ListItem>
            ));
          })}
        <Divider />
        {isMamacleanReservation && (
          <MamacleanReservationInfobox reservation={serviceReservation} />
        )}
        {reservationServices && reservationServices.length > 0 && (
          <ListItem>
            <ListItemIcon>
              <FormatListBulletedIcon />
            </ListItemIcon>
            <Typography variant="caption">
              Servizi richiesti:
              <ul>
                {reservationServices.map((item) => {
                  const prefix = item.quantity ? `${item.quantity} x ` : "";
                  return (
                    <li>
                      {prefix}
                      {item.label} - {currency(item.price)}
                    </li>
                  );
                })}
              </ul>
            </Typography>
          </ListItem>
        )}
        {reservationProducts && reservationProducts.length > 0 && (
          <ListItem>
            <ListItemIcon>
              <FormatListBulletedIcon />
            </ListItemIcon>
            <Typography variant="caption">
              Prodotti acquistati
              <ul>
                {reservationProducts.map((item) => (
                  <li>
                    {item.label} - {currency(item.price)}
                  </li>
                ))}
              </ul>
            </Typography>
          </ListItem>
        )}
        {serviceReservation.additionalDetails && (
          <ListItem>
            <ListItemIcon>
              <AssignmentLateIcon />
            </ListItemIcon>
            <Typography variant="caption">
              Note aggiuntive: {serviceReservation.additionalDetails}
            </Typography>
          </ListItem>
        )}
      </List>
    </Paper>
  );
}
